import { useMemo } from 'react';
import { useAssignments } from "./hooks/useAssignments";
import AssignmentSearch from "./components/assignmentSearch";
import AssignmentsCalendar from "./components/assignmentsCalendar";
import { useLocation } from 'react-router-dom';
import './styles/Assignments.css';
import type { DispatchAssignmentParams, DispatchAssignment } from './types/assignment.types';
import Loader from '../../components/common/Loader';

function useQueryParams() {
    const { search } = useLocation();

    // useMemo caches the parsed query params so this object is only
    // recreated when the URL's search string actually changes.
    return useMemo(() => {
        const params = new URLSearchParams(search);

        return {
            period: params.get('period'),   // 'current' | 'next' | 'previous' | null
            weekStart: params.get('weekStart'),     // YYYY-MM-DD
            serviceAreaId: params.get('serviceAreaId')
        };
    }, [search]);
}

export default function Assignments() {
    const query : DispatchAssignmentParams = useQueryParams();

    const { assignmentsData,
        serviceAreasData,
        employeesData,
        loading,
        error,
        updateStartDateParam,
        searchServiceAreaId,
        getDispatchAssignments,
        getDispatchServiceAreas,
        getDispatchAssignmentEmployees,
        saveDispatchAssignments,
        updateServiceAreaIdParam,
        navigatePrevious,
        navigateNext,
        searchWeekStart,
        showAssignmentModal,
        toggleShowAssignmentModal,
        updateStartDateField, updateEndDateField, updateEmployeeIdField,
        startDateField, endDateField, employeeIdField,
        rankField, updateRankField, createAssignment, clearStartDateField, clearEndDateField, clearEmployeeIdField, clearRankField,
        showEditAssignmentModal, toggleShowEditAssignmentsModal,
        startDateEditField, endDateEditField, rankEditField,
        updateStartDateEditField, updateEndDateEditField, updateRankEditField,
        assignmentsRef,
        selectedEditAssignment, updateSelectedEditAssignment, editAssignment,
        deleteAssignment,
        showConfirmDeleteModal, toggleShowConfirmDeleteModal,
        loadingCalendar, toggleLoadingCalendar
    } = useAssignments(query);
    
    return (
        <div className='dispatch-assignments-container'>
            <div className='dispatch-assignments-header-wrapper'>
                <h2>Assignments</h2>
            </div>
            {loading && (
                <div>
                    <Loader title="Loading Dispatch Assignments" />
                </div>
            )}
            {!loading && (
                <>
                    <AssignmentSearch
                        searchWeekStart={searchWeekStart}
                        updateStartDateParam={updateStartDateParam}
                        searchServiceAreaId={searchServiceAreaId}
                        getDispatchAssignments={getDispatchAssignments}
                        serviceAreasData={serviceAreasData}
                        updateServiceAreaIdParam={updateServiceAreaIdParam}
                        toggleLoadingCalendar={(loading: boolean) => toggleLoadingCalendar(loading)}
                    />
                    <AssignmentsCalendar
                        assignmentsData={assignmentsData}
                        navigatePrevious={navigatePrevious}
                        navigateNext={navigateNext}
                        showAssignmentModal={showAssignmentModal}
                        toggleShowAssignmentModal={(show: boolean) => toggleShowAssignmentModal(show)}
                        startDateField={startDateField}
                        endDateField={endDateField}
                        employeeIdField={employeeIdField}
                        updateStartDateField={(date: string) => updateStartDateField(date)}
                        updateEndDateField={(date: string) => updateEndDateField(date)}
                        updateEmployeeIdField={(id: string) => updateEmployeeIdField(id)}
                        employeesData={employeesData}
                        rankField={rankField}
                        updateRankField={(rank: string) => updateRankField(rank)}
                        createAssignment={createAssignment}
                        searchWeekStart={searchWeekStart}
                        searchServiceAreaId={searchServiceAreaId}
                        clearStartDateField={clearStartDateField}
                        clearEndDateField={clearEndDateField}
                        clearEmployeeIdField={clearEmployeeIdField}
                        clearRankField={clearRankField}
                        showEditAssignmentModal={showEditAssignmentModal}
                        toggleShowEditAssignmentModal={(show: boolean) => toggleShowEditAssignmentsModal(show)}
                        startDateEditField={startDateEditField}
                        endDateEditField={endDateEditField}
                        rankEditField={rankEditField}
                        updateStartDateEditField={(date: string) => updateStartDateEditField(date)}
                        updateEndDateEditField={(date: string) => updateEndDateEditField(date)}
                        updateRankEditField={(rank: string) => updateRankEditField(rank)}
                        assignmentsRef={assignmentsRef}                        
                        selectedEditAssignment={selectedEditAssignment}
                        updateSelectedEditAssignment={(assignment: DispatchAssignment) => updateSelectedEditAssignment(assignment)}
                        editAssignment={editAssignment}
                        deleteAssignment={deleteAssignment}
                        showConfirmDeleteModal={showConfirmDeleteModal}
                        toggleShowConfirmDeleteModal={(show: boolean) => toggleShowConfirmDeleteModal(show)}
                        loadingCalendar={loadingCalendar}
                        toggleLoadingCalendar={(loading: boolean) => toggleLoadingCalendar(loading)}
                    />            
                </>
            )}
        </div>
    )
}